import { useState } from "react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  WorkbenchPage,
  WorkbenchPageContent,
  WorkbenchPageHeader,
} from "../ui/workbench-page";
import type { Book } from "../../types";
import { cn } from "../../lib/utils";
import { buildPlanningCards } from "../../lib/phaseRuntime";
import { getChapterScenes } from "../../../shared/chapterScenes";

export type PlanningMode = "corkboard" | "outliner";

const STATUS_LABEL: Record<string, string> = {
  draft: "Draft",
  in_review: "In review",
  final: "Final",
};

export function PlanningPanel({
  book,
  activeChapterId,
  onSelectChapter,
  onUpdateChapterSummary,
  onUpdateScene,
  onDetectScenes,
  mode: controlledMode,
  onModeChange,
  storyCraftAverages,
  promiseDebtByChapter,
}: {
  book: Book;
  activeChapterId: string | null;
  onSelectChapter: (chapterId: string) => void;
  onUpdateChapterSummary: (chapterId: string, summary: string) => void;
  onUpdateScene?: (
    chapterId: string,
    sceneId: string,
    patch: { title?: string; summary?: string }
  ) => void;
  onDetectScenes?: (chapterId: string) => void;
  mode?: PlanningMode;
  onModeChange?: (mode: PlanningMode) => void;
  storyCraftAverages?: Record<string, number | null>;
  /** Open/unkept narrative promise counts per chapter. */
  promiseDebtByChapter?: Record<string, number>;
}) {
  const [internalMode, setInternalMode] = useState<PlanningMode>("corkboard");
  const mode = controlledMode ?? internalMode;

  function setMode(next: PlanningMode): void {
    if (onModeChange) onModeChange(next);
    else setInternalMode(next);
  }

  const cards = buildPlanningCards(book);
  const ordered = [...cards].sort((a, b) => a.order - b.order);
  const activeCard = ordered.find((c) => c.id === activeChapterId);
  const activeChapter =
    activeChapterId != null ? book.chapters.find((c) => c.id === activeChapterId) : undefined;
  const activeScenes = activeChapter ? getChapterScenes(activeChapter) : [];
  const activeSceneCount = activeScenes.length;

  return (
    <section className="h-full min-h-0" aria-label="Planning">
      <WorkbenchPage>
        <WorkbenchPageHeader
          title="Planning"
          description={
            <>
              Corkboard and outliner views of chapter summaries.
              {activeCard
                ? ` Active: ${activeCard.title}${activeSceneCount ? ` · ${activeSceneCount} scenes` : ""}.`
                : ""}
            </>
          }
          actions={
            <>
            <Button
              size="sm"
              type="button"
              variant={mode === "corkboard" ? "default" : "secondary"}
              onClick={() => setMode("corkboard")}
              aria-pressed={mode === "corkboard"}
            >
              Corkboard
            </Button>
            <Button
              size="sm"
              type="button"
              variant={mode === "outliner" ? "default" : "secondary"}
              onClick={() => setMode("outliner")}
              aria-pressed={mode === "outliner"}
            >
              Outliner
            </Button>
            {onDetectScenes && activeChapterId ? (
              <Button
                size="sm"
                type="button"
                variant="outline"
                onClick={() => onDetectScenes(activeChapterId)}
                title="Split chapter on *** or Scene markers"
              >
                Detect scenes
              </Button>
            ) : null}
            </>
          }
        />

        <WorkbenchPageContent>
          <div className="flex flex-col gap-2.5">
          {ordered.length === 0 ? (
            <p className="text-xs text-muted-foreground">No chapters yet.</p>
          ) : mode === "corkboard" ? (
            <div
              className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3"
              role="list"
              aria-label="Chapter corkboard"
            >
              {ordered.map((card) => {
                const active = card.id === activeChapterId;
                const avg = storyCraftAverages?.[card.id];
                const promiseDebt = promiseDebtByChapter?.[card.id] ?? 0;
                return (
                  <Button
                    key={card.id}
                    type="button"
                    role="listitem"
                    variant="ghost"
                    className={cn(
                      "h-auto flex cursor-pointer flex-col gap-2 rounded-lg border border-border/70 bg-card px-3.5 py-3 text-left text-foreground transition-[border-color,background-color,box-shadow] hover:border-primary/30 hover:bg-accent/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
                      active && "border-primary/60 bg-accent/55 ring-1 ring-primary/20"
                    )}
                    onClick={() => onSelectChapter(card.id)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-[0.8125rem] font-semibold leading-snug">{card.title}</span>
                      <span className="flex shrink-0 items-center gap-1.5">
                        {typeof avg === "number" && Number.isFinite(avg) ? (
                          <Badge
                            variant="secondary"
                            className="h-auto px-2 py-0.5 text-[0.6875rem] tabular-nums"
                            title="Story Craft average"
                          >
                            {avg.toFixed(1)}
                          </Badge>
                        ) : null}
                        {promiseDebt > 0 ? (
                          <Badge
                            variant="outline"
                            className="h-auto border-primary/30 bg-primary/10 px-2 py-0.5 text-[0.6875rem] text-primary tabular-nums"
                            title="Open narrative promises from Story Craft"
                          >
                            {promiseDebt} promise{promiseDebt === 1 ? "" : "s"}
                          </Badge>
                        ) : null}
                        <span className="text-[0.72rem] text-muted-foreground">#{card.order}</span>
                      </span>
                    </div>
                    <p className="m-0 line-clamp-4 min-h-12 text-xs leading-[1.45] text-muted-foreground">
                      {card.summary.trim() || "No summary yet."}
                    </p>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge
                        variant="outline"
                        className="h-auto px-2 py-0.5 text-[0.6875rem] font-normal"
                      >
                        {STATUS_LABEL[card.status] ?? card.status}
                      </Badge>
                      {card.tags.slice(0, 3).map((tag) => (
                        <Badge
                          key={tag}
                          variant="secondary"
                          className="h-auto px-2 py-0.5 text-[0.6875rem] font-normal text-muted-foreground"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </Button>
                );
              })}
            </div>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-2.5 p-0" aria-label="Chapter outliner">
              {ordered.map((card) => {
                const active = card.id === activeChapterId;
                const avg = storyCraftAverages?.[card.id];
                return (
                  <li
                    key={card.id}
                    className={cn(
                      "rounded-lg border border-border/70 bg-card px-3.5 py-3 transition-colors",
                      active && "border-primary/50 bg-accent/40"
                    )}
                  >
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        className="h-auto cursor-pointer border-0 bg-transparent p-0 text-left text-[0.8125rem] font-semibold text-inherit underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                        onClick={() => onSelectChapter(card.id)}
                      >
                        {card.order}. {card.title}
                      </Button>
                      <Badge
                        variant="outline"
                        className="h-auto px-2 py-0.5 text-[0.6875rem] font-normal"
                      >
                        {STATUS_LABEL[card.status] ?? card.status}
                      </Badge>
                      {typeof avg === "number" && Number.isFinite(avg) ? (
                        <Badge
                          variant="secondary"
                          className="h-auto px-2 py-0.5 text-[0.6875rem] tabular-nums"
                          title="Story Craft average"
                        >
                          {avg.toFixed(1)}
                        </Badge>
                      ) : null}
                    </div>
                    <Textarea
                      aria-label={`Summary for ${card.title}`}
                      rows={3}
                      placeholder="Chapter summary…"
                      value={card.summary}
                      onChange={(e) => onUpdateChapterSummary(card.id, e.target.value)}
                      onFocus={() => {
                        if (card.id !== activeChapterId) onSelectChapter(card.id);
                      }}
                    />
                  </li>
                );
              })}
            </ul>
          )}

          {activeChapterId && activeChapter ? (
            <div className="mt-5 border-t border-border/60 pt-4" aria-label="Chapter scenes">
              <div className="mb-3 flex flex-wrap items-start justify-between gap-2.5">
                <div>
                  <h3 className="text-sm font-medium">
                    Scenes{activeCard ? ` · ${activeCard.title}` : ""}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Edit scene titles and summaries for the active chapter.
                  </p>
                </div>
                {onDetectScenes ? (
                  <Button
                    size="sm"
                    type="button"
                    variant="outline"
                    onClick={() => onDetectScenes(activeChapterId)}
                  >
                    Detect scenes
                  </Button>
                ) : null}
              </div>
              {activeScenes.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No scenes yet — Detect scenes or add scene breaks with *** in the manuscript.
                </p>
              ) : (
                <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
                  {activeScenes.map((scene) => (
                    <li
                      key={scene.id}
                      className="rounded-lg border border-border/60 bg-card/90 px-3.5 py-3"
                    >
                      <div className="mb-2 flex items-center gap-2">
                        <span className="text-[0.72rem] text-muted-foreground tabular-nums">
                          #{scene.order}
                        </span>
                        <Input
                          aria-label={`Title for scene ${scene.order}`}
                          className="h-8 text-sm"
                          value={scene.title}
                          disabled={!onUpdateScene}
                          onChange={(e) =>
                            onUpdateScene?.(activeChapterId, scene.id, {
                              title: e.target.value,
                            })
                          }
                        />
                      </div>
                      <Textarea
                        aria-label={`Summary for scene ${scene.order}`}
                        rows={2}
                        placeholder="Scene summary…"
                        value={scene.summary ?? ""}
                        disabled={!onUpdateScene}
                        onChange={(e) =>
                          onUpdateScene?.(activeChapterId, scene.id, {
                            summary: e.target.value,
                          })
                        }
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
          </div>
        </WorkbenchPageContent>
      </WorkbenchPage>
    </section>
  );
}
